import { CustomButton } from "@/components/custom/custom-button";
import { CustomErrorMessage } from "@/components/custom/custom-error-message";
import { FormSelectInput } from "@/components/custom/custom-form-select-input";
import { FormTextInput } from "@/components/custom/custom-form-text-input";
import { FormTextArea } from "@/components/custom/custom-form-textarea";
import { useLedgerAccounts } from "@/hooks/use-ledger-accounts";
import { usePostJournal } from "@/hooks/use-ledger-entries";
import { useLedgerEnv } from "@/hooks/use-ledger-env";
import { showToast } from "@/redux/actions/toast-actions";
import { postJournalSchema } from "@/schemas/ledger.schema";
import { formatCurrency } from "@/utils/format-currency";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Chip,
  Stack,
  Divider,
  styled,
} from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    padding: theme.spacing(2),
    maxWidth: 560,
    width: "100%",
  },
}));

// Stable, unique per real-world transfer. Regenerated only when the modal opens.
const generateReference = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `JREF-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
};

const PostJournalDialog = ({ open, onClose, defaultDebit, defaultCredit }) => {
  const dispatch = useDispatch();
  const { environment } = useLedgerEnv();
  const mutation = usePostJournal();

  // journalReference is the idempotency key. Fresh on open, stable while open,
  // so a network-blip retry re-uses it (safe dedupe) but a new transfer is new.
  const [journalReference, setJournalReference] = useState(generateReference);

  // Backend rejection (e.g. 422 insufficient balance) shown in-modal via the
  // shared CustomErrorMessage banner, not just a transient toast.
  const [submitError, setSubmitError] = useState("");

  // Populate the pickers from the accounts list (large page for v1).
  // TODO: if the account count grows, switch to a searchable/async picker.
  const { data: accountsRes } = useLedgerAccounts(
    { environment, page: 0, size: 100 },
    { enabled: open },
  );
  const accounts = accountsRes?.data ?? [];

  const accountOptions = accounts.map((a) => ({
    value: a.accountNumber,
    label: `${a.accountName} (${a.accountNumber}) • ${formatCurrency(
      a.balance ?? 0,
      a.currency ?? "NGN",
    )}`,
  }));

  const formik = useFormik({
    initialValues: {
      debitAccountNumber: defaultDebit ?? "",
      creditAccountNumber: defaultCredit ?? "",
      amount: "",
      narration: "",
    },
    enableReinitialize: true,
    validationSchema: postJournalSchema,
    validateOnMount: true,
    onSubmit: (values) => {
      setSubmitError("");
      mutation.mutate(
        {
          environment,
          journalReference,
          debitAccountNumber: values.debitAccountNumber,
          creditAccountNumber: values.creditAccountNumber,
          amount: Number(values.amount),
          narration: values.narration || undefined,
        },
        {
          onSuccess: (data) => {
            const replayed = data?.message === "Journal already posted";
            dispatch(
              showToast(
                replayed
                  ? "This journal was already posted (no double entry)."
                  : (data?.message ?? "Journal posted"),
                "success",
              ),
            );
            // Clear the form and start a fresh reference so the next post is a
            // new transfer (not an idempotent replay of the one just posted).
            formik.resetForm();
            setSubmitError("");
            setJournalReference(generateReference());
            onClose();
          },
          onError: (err) => {
            // Surface the backend reason in the in-modal banner, and also flag
            // the most relevant field. Never clear the form so the user can fix
            // one input and resubmit with the same reference.
            const msg = err?.message;
            switch (err?.statusCode) {
              case 400:
                formik.setFieldError(
                  "creditAccountNumber",
                  msg ?? "Debit and credit accounts must differ.",
                );
                setSubmitError(msg ?? "Debit and credit accounts must differ.");
                break;
              case 404:
                formik.setFieldError(
                  "debitAccountNumber",
                  msg ??
                    "One of these accounts was not found in this environment.",
                );
                setSubmitError(
                  msg ??
                    "One of these accounts was not found in this environment.",
                );
                break;
              case 422:
                // Inactive account or insufficient balance on the debit account.
                formik.setFieldError(
                  "debitAccountNumber",
                  msg ??
                    "Both accounts must be ACTIVE and the debit must have enough balance.",
                );
                setSubmitError(
                  msg ??
                    "Both accounts must be ACTIVE and the debit must have enough balance.",
                );
                break;
              default:
                setSubmitError(msg ?? "Failed to post journal");
            }
          },
        },
      );
    },
  });

  // Regenerate the reference only on open. Reset form to its defaults.
  useEffect(() => {
    if (open) {
      setJournalReference(generateReference());
      setSubmitError("");
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    if (mutation.isPending) return;
    formik.resetForm();
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}
      >
        <Box>
          <DialogTitle
            sx={{
              p: 0,
              fontSize: "1.1rem",
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            Post Journal
          </DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1} mt={0.75}>
            <Typography variant="body2" color="text.secondary">
              Move funds in
            </Typography>
            <Chip
              label={environment}
              size="small"
              sx={{
                height: 22,
                fontWeight: 600,
                fontSize: "0.7rem",
                backgroundColor:
                  environment === "SANDBOX" ? "#fffbeb" : "#eef2ff",
                color: environment === "SANDBOX" ? "#92400e" : "#3730a3",
              }}
            />
          </Stack>
        </Box>

        {submitError && <CustomErrorMessage message={submitError} />}

        <Stack direction="column" spacing={2} alignItems="stretch">
          <FormSelectInput
            formik={formik}
            name="debitAccountNumber"
            label="Debit (balance goes down)"
            options={accountOptions}
          />
          <FormSelectInput
            formik={formik}
            name="creditAccountNumber"
            label="Credit (balance goes up)"
            options={accountOptions}
          />
        </Stack>

        <FormTextInput
          formik={formik}
          name="amount"
          label="Amount"
          type="number"
          placeholder="0.00"
        />
        <FormTextArea
          formik={formik}
          name="narration"
          label="Narration (optional)"
          rows={2}
        />

        <Divider />
        <Typography variant="caption" color="text.secondary">
          Reference: {journalReference}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 2, pt: 1, pb: 1, gap: 1 }}>
        <CustomButton
          variantType="outline"
          size="small"
          onClick={handleClose}
          disabled={mutation.isPending}
          sx={{ px: 2 }}
        >
          Cancel
        </CustomButton>
        <CustomButton
          size="small"
          disabled={!formik.isValid || mutation.isPending}
          isLoading={mutation.isPending}
          onClick={formik.handleSubmit}
          sx={{ px: 2 }}
        >
          {mutation.isPending ? "Posting..." : "Post Journal"}
        </CustomButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default PostJournalDialog;
